import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Layout from '@/components/layout/Layout';
import Login from '@/pages/auth/Login';
import Dashboard from '@/pages/dashboard/Dashboard';

// Lazy load pages for better performance
const Users = React.lazy(() => import('@/pages/users/Users'));
const UserDetails = React.lazy(() => import('@/pages/users/UserDetails'));
const Transactions = React.lazy(() => import('@/pages/transactions/Transactions'));
const TransactionDetails = React.lazy(() => import('@/pages/transactions/TransactionDetails'));
const Games = React.lazy(() => import('@/pages/games/Games'));
const LiveGames = React.lazy(() => import('@/pages/games/LiveGames'));
const ChickenRoadHistory = React.lazy(() => import('@/pages/games/ChickenRoadHistory'));
const Wallets = React.lazy(() => import('@/pages/wallets/Wallets'));
const Analytics = React.lazy(() => import('@/pages/analytics/Analytics'));
const Reports = React.lazy(() => import('@/pages/reports/Reports'));
const System = React.lazy(() => import('@/pages/system/System'));
const Settings = React.lazy(() => import('@/pages/settings/Settings'));
const Alerts = React.lazy(() => import('@/pages/alerts/Alerts'));

// Coin System Pages
const PurchaseApprovals = React.lazy(() => import('@/pages/coins/PurchaseApprovals').then(m => ({ default: m.PurchaseApprovals })));
const WithdrawalApprovals = React.lazy(() => import('@/pages/coins/WithdrawalApprovals').then(m => ({ default: m.WithdrawalApprovals })));
const OfferManagement = React.lazy(() => import('@/pages/coins/OfferManagement').then(m => ({ default: m.OfferManagement })));
const BankAccountManagement = React.lazy(() => import('@/pages/coins/BankAccountManagement').then(m => ({ default: m.BankAccountManagement })));
const UPIAccountManagement = React.lazy(() => import('@/pages/coins/UPIAccountManagement').then(m => ({ default: m.UPIAccountManagement })));

// Loading component
const Loading: React.FC = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <React.Suspense fallback={<Loading />}>
      {children}
    </React.Suspense>
  );
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                {/* Dashboard */}
                <Route path="/dashboard" element={<Dashboard />} />

                {/* User Management */}
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<UserDetails />} />

                {/* Transactions */}
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/transactions/:id" element={<TransactionDetails />} />

                {/* Games */}
                <Route path="/games" element={<Games />} />
                <Route path="/games/live" element={<LiveGames />} />
                <Route path="/games/chicken-road" element={<ChickenRoadHistory />} />

                {/* Wallets */}
                <Route path="/wallets" element={<Wallets />} />

                {/* Coin System */}
                <Route path="/coins/purchases" element={<PurchaseApprovals />} />
                <Route path="/coins/withdrawals" element={<WithdrawalApprovals />} />
                <Route path="/coins/offers" element={<OfferManagement />} />
                <Route path="/coins/bank-accounts" element={<BankAccountManagement />} />
                <Route path="/coins/upi-accounts" element={<UPIAccountManagement />} />

                {/* Analytics & Reports */}
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/reports" element={<Reports />} />

                {/* System */}
                <Route path="/system" element={<System />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/settings" element={<Settings />} />

                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 404 - Not Found */}
                <Route
                  path="*"
                  element={
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-slate-300 mb-4">404</h1>
                        <h2 className="text-2xl font-semibold text-slate-700 mb-2">Page Not Found</h2>
                        <p className="text-slate-500 mb-4">
                          The page you are looking for does not exist.
                        </p>
                        <button
                          onClick={() => window.history.back()}
                          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;